import React, { createContext, useContext, useReducer, useEffect, useCallback, useState, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import { notificationApi } from '../services/notificationApi';
import { usePushRegistration } from '../hooks/usePushRegistration';
import { useNotificationClickBridge } from '../hooks/useNotificationClickBridge';
import { triggerAccessRefresh } from '../hooks/useAccess';
import { toast } from 'react-toastify';
import type { PushPermission } from '../config/firebase';
import EnablePushDialog from '../components/EnablePushDialog';

// Types
export interface Notification {
  _id: string;
  title: string;
  message: string;
  type: string;
  isRead: boolean;
  createdAt: string;
  data?: any;
  context?: 'user_panel' | 'seller_panel' | 'admin_panel';
}

export interface NotificationStats {
  total: number;
  unread: number;
  userPanel: number;
  sellerPanel: number;
  adminPanel: number;
}

interface NotificationState {
  notifications: Notification[];
  stats: NotificationStats;
  loading: boolean;
  error: string | null;
  socket: Socket | null;
  connected: boolean;
}

type NotificationAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_NOTIFICATIONS'; payload: Notification[] }
  | { type: 'ADD_NOTIFICATION'; payload: Notification }
  | { type: 'UPDATE_NOTIFICATION'; payload: { id: string; updates: Partial<Notification> } }
  | { type: 'REMOVE_NOTIFICATION'; payload: string }
  | { type: 'SET_STATS'; payload: NotificationStats }
  | { type: 'SET_SOCKET'; payload: Socket | null }
  | { type: 'SET_CONNECTED'; payload: boolean }
  | { type: 'MARK_ALL_AS_READ'; payload: string };

const initialState: NotificationState = {
  notifications: [],
  stats: {
    total: 0,
    unread: 0,
    userPanel: 0,
    sellerPanel: 0,
    adminPanel: 0,
  },
  loading: false,
  error: null,
  socket: null,
  connected: false,
};

const notificationReducer = (state: NotificationState, action: NotificationAction): NotificationState => {
  // Defensive: API/socket payloads can occasionally arrive in an unexpected shape
  // (e.g. an object instead of an array). Always operate on a guaranteed array so a
  // single bad payload can't crash the provider with "state.notifications is not iterable".
  const list: Notification[] = Array.isArray(state.notifications) ? state.notifications : [];

  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };

    case 'SET_NOTIFICATIONS':
      return {
        ...state,
        notifications: Array.isArray(action.payload) ? action.payload : [],
        loading: false,
      };

    case 'ADD_NOTIFICATION':
      // Skip duplicates by _id so a repeated socket/push event can't pile up the list.
      if (action.payload && list.some((n) => n._id === action.payload._id)) {
        return state;
      }
      return {
        ...state,
        notifications: [action.payload, ...list],
        stats: {
          ...state.stats,
          total: state.stats.total + 1,
          unread: state.stats.unread + (action.payload.isRead ? 0 : 1),
        },
      };

    case 'UPDATE_NOTIFICATION':
      return {
        ...state,
        notifications: list.map((notification) =>
          notification._id === action.payload.id ? { ...notification, ...action.payload.updates } : notification
        ),
      };

    case 'REMOVE_NOTIFICATION':
      const removedNotification = list.find((n) => n._id === action.payload);
      return {
        ...state,
        notifications: list.filter((n) => n._id !== action.payload),
        stats: {
          ...state.stats,
          total: Math.max(0, state.stats.total - 1),
          unread: Math.max(0, state.stats.unread - (removedNotification && !removedNotification.isRead ? 1 : 0)),
        },
      };

    case 'SET_STATS':
      return { ...state, stats: action.payload };

    case 'SET_SOCKET':
      return { ...state, socket: action.payload };

    case 'SET_CONNECTED':
      return { ...state, connected: action.payload };

    case 'MARK_ALL_AS_READ':
      return {
        ...state,
        notifications: list.map((notification) =>
          notification.context === action.payload ? { ...notification, isRead: true } : notification
        ),
        stats: {
          ...state.stats,
          unread: list.filter((n) => n.context !== action.payload && !n.isRead).length,
        },
      };

    default:
      return state;
  }
};

interface NotificationContextType extends NotificationState {
  // Actions
  fetchNotifications: (context?: string, page?: number, limit?: number) => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: (context: string) => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  getUnreadCount: (context?: string) => Promise<void>;

  // Socket methods
  connectSocket: () => void;
  disconnectSocket: () => void;

  // Utility methods
  getNotificationsByContext: (context: string) => Notification[];
  getUnreadByContext: (context: string) => number;

  // Web push (FCM) controls — consumed by <EnablePushButton /> and <EnablePushDialog />
  pushPermission: PushPermission;
  pushSupported: boolean;
  pushConfigured: boolean;
  pushBusy: boolean;
  enablePush: () => Promise<PushPermission>;
  pushDialogOpen: boolean;
  openPushDialog: () => void;
  closePushDialog: () => void;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const { user } = useAuth();
  // Track notification IDs we've already shown a browser push for, so a repeated
  // socket emission of the same notification doesn't spam the desktop popup.
  const pushedIdsRef = React.useRef<Set<string>>(new Set());
  // Hold the live socket in a ref so connect/disconnect don't depend on state.socket
  // (which would re-create the callbacks → re-run the connect effect → reconnect loop).
  const socketRef = React.useRef<Socket | null>(null);

  // Helper: safely detect Notification API support to avoid crashes on mobile
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isNotificationSupported = (): boolean => {
    try {
      return typeof window !== 'undefined' && 'Notification' in window;
    } catch {
      return false;
    }
  };

  // Socket connection management
  const connectSocket = useCallback(() => {
    const token = localStorage.getItem('accessToken');

    // Guard against duplicate connections using the ref (NOT state.socket — that
    // would put state in this callback's deps and cause a reconnect loop).
    if (!token || socketRef.current) return;

    // Connect to the DEFAULT namespace (not /notifications — that legacy namespace is unused here).
    // The backend auto-joins this socket to room `${userId}_admin_panel`
    // and emits notifications there as the `new_notification` event.
    // REACT_APP_API_URL is likehttps://api.gamizo.ir/api → strip the trailing /api so
    // Socket.IO connects to the server origin, not the /api path.
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const socketOrigin = apiUrl.replace(/\/api\/?$/, '');

    const newSocket = io(socketOrigin, {
      auth: { token },
      // ⚠️ فقط polling — نه websocket.
      //
      // reverse-proxy جلوی api.gamizo.ir هدر WebSocket Upgrade را پاس نمی‌دهد:
      // به‌جای 101، حدود ۱۲ ثانیه hang می‌کند و بعد می‌بندد. با
      // `transports: ['websocket']` هیچ fallback ای وجود نداشت، پس سوکت ادمین
      // اصلاً وصل نمی‌شد و **هیچ اعلان ریل‌تایمی به پنل ادمین نمی‌رسید**.
      // figano-web قبلاً به همین نتیجه رسیده بود (lib/socketManager.ts).
      // اگر روزی هاست از WS پشتیبانی کرد → ['polling', 'websocket'].
      transports: ['polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
    });

    newSocket.on('connect', () => {
      console.log('Notification socket connected');
      dispatch({ type: 'SET_CONNECTED', payload: true });
      // بک‌اند در getUserRooms برای ادمین خودکار join می‌کند، ولی emit صریح
      // هزینه‌ای ندارد و اگر نقش کاربر بعد از اتصال تغییر کند هم درست می‌ماند.
      newSocket.emit('join_context_room', 'admin_panel');
    });

    newSocket.on('connect_error', (err: any) => {
      console.warn('Notification socket connect_error:', err?.message || err);
      dispatch({ type: 'SET_CONNECTED', payload: false });
    });

    newSocket.on('disconnect', () => {
      console.log('Notification socket disconnected');
      dispatch({ type: 'SET_CONNECTED', payload: false });
    });

    const handleIncoming = (notification: Notification) => {
      if (!notification || !notification._id) return;
      dispatch({ type: 'ADD_NOTIFICATION', payload: notification });

      // Show browser notification once per unique notification id (dedupe repeats).
      if (pushedIdsRef.current.has(notification._id)) return;
      pushedIdsRef.current.add(notification._id);
      try {
        // فقط تبِ قابل‌مشاهده از روی سوکت اعلان سیستمی می‌سازد؛ تب پنهان را
        // Service Worker پوش (FCM) پوشش می‌دهد — کشیدن اینجا هم = اعلان تکراری.
        // tag با SW یکسان است تا اگر هر دو هم بزنند، مرورگر جایگزین کند نه انباشته.
        if (
          isNotificationSupported() &&
          window.Notification.permission === 'granted' &&
          typeof document !== 'undefined' &&
          document.visibilityState === 'visible'
        ) {
          new window.Notification(notification.title, {
            body: notification.message,
            icon: '/logo192.png',
            tag: `gz-n-${notification._id}`, // collapse duplicates at the OS level too
            dir: 'rtl',
            lang: 'fa',
          });
        }
      } catch (e) {
        console.warn('Browser Notification not supported or blocked:', e);
      }
    };

    // بک‌اند هر دو نام را emit می‌کند (`notification` قدیمی، `new_notification`
    // جدید). ادمین فقط به دومی گوش می‌داد؛ هر رویدادی که با نام اول می‌آمد
    // بی‌صدا از دست می‌رفت.
    newSocket.on('new_notification', handleIncoming);
    newSocket.on('notification', handleIncoming);

    // شمارندهٔ سرور — منبع حقیقت بج، مستقل از اینکه خود اعلان رسیده باشد یا نه.
    newSocket.on('notification_count_update', () => {
      void getUnreadCount('admin_panel');
    });

    newSocket.on('error', (error: any) => {
      console.error('Socket error:', error);
      dispatch({ type: 'SET_ERROR', payload: 'خطا در اتصال به سرور نوتیفیکیشن' });
    });

    // ── رویدادهای تغییر دسترسی ──────────────────────────────────────────
    // بک‌اند بعد از تغییر نقش / اعطا یا لغو گرنت، روی room `user_<id>` این
    // رویدادها را می‌فرستد. اسنپ‌شات /admin/me/access همین‌جا تازه می‌شود تا
    // منو و دکمه‌ها بدون رفرش صفحه به‌روز شوند. (useAccess به دنبال
    // window.__figanoSocket می‌گشت که هیچ‌جا ست نمی‌شد ⇒ هرگز به این
    // رویدادها گوش نمی‌داد و فقط poll ۶۰ ثانیه‌ای کار می‌کرد.)
    const ACCESS_EVENT_TOAST: Record<string, string> = {
      'role:updated': 'نقش یا دسترسی‌های شما تغییر کرد؛ منوها به‌روز شدند.',
      'grant:added': 'یک دسترسی موقت به شما داده شد؛ منوها به‌روز شدند.',
      'grant:revoked': 'یک دسترسی موقت شما لغو شد.',
      'permission:changed': 'دسترسی‌های شما تغییر کرد؛ منوها به‌روز شدند.',
    };
    Object.entries(ACCESS_EVENT_TOAST).forEach(([event, msg]) => {
      newSocket.on(event, () => {
        triggerAccessRefresh();
        toast.info(msg, { toastId: `access-${event}` });
      });
    });
    (window as any).__figanoSocket = newSocket;

    socketRef.current = newSocket;
    dispatch({ type: 'SET_SOCKET', payload: newSocket });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disconnectSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      if ((window as any).__figanoSocket === socketRef.current) (window as any).__figanoSocket = null;
      socketRef.current = null;
      dispatch({ type: 'SET_SOCKET', payload: null });
      dispatch({ type: 'SET_CONNECTED', payload: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // API methods
  const fetchNotifications = useCallback(async (context?: string, page = 1, limit = 20) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      const response = await notificationApi.getNotifications(context, { page, limit });

      if (response.success) {
        dispatch({ type: 'SET_NOTIFICATIONS', payload: response.data.notifications });
      } else {
        dispatch({ type: 'SET_ERROR', payload: response.message || 'خطا در دریافت نوتیفیکیشن‌ها' });
      }
    } catch (error: any) {
      console.error('Error fetching notifications:', error);
      dispatch({ type: 'SET_ERROR', payload: 'خطا در دریافت نوتیفیکیشن‌ها' });
    }
  }, []);

  const markAsRead = useCallback(
    async (notificationId: string) => {
      try {
        const response = await notificationApi.markAsRead(notificationId);

        if (response.success) {
          dispatch({
            type: 'UPDATE_NOTIFICATION',
            payload: { id: notificationId, updates: { isRead: true } },
          });

          // Update stats
          dispatch({
            type: 'SET_STATS',
            payload: {
              ...state.stats,
              unread: Math.max(0, state.stats.unread - 1),
            },
          });
        }
      } catch (error: any) {
        console.error('Error marking notification as read:', error);
      }
    },
    [state.stats]
  );

  const markAllAsRead = useCallback(async (context: string) => {
    try {
      const response = await notificationApi.markAllAsRead(context);

      if (response.success) {
        dispatch({ type: 'MARK_ALL_AS_READ', payload: context });
      }
    } catch (error: any) {
      console.error('Error marking all notifications as read:', error);
    }
  }, []);

  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      const response = await notificationApi.deleteNotification(notificationId);

      if (response.success) {
        dispatch({ type: 'REMOVE_NOTIFICATION', payload: notificationId });
      }
    } catch (error: any) {
      console.error('Error deleting notification:', error);
    }
  }, []);

  const getUnreadCount = useCallback(
    async (context?: string) => {
      try {
        const response = await notificationApi.getUnreadCount(context);

        if (response.success) {
          // Update only the unread count in stats
          dispatch({
            type: 'SET_STATS',
            payload: {
              ...state.stats,
              unread: response.data.count,
            },
          });
        }
      } catch (error: any) {
        console.error('Error getting unread count:', error);
      }
    },
    [state.stats]
  );

  // Utility methods (defensive: state.notifications is always an array via the reducer,
  // but guard here too so a stale/hot-reloaded state can't crash a consumer).
  const getNotificationsByContext = useCallback(
    (context: string) => {
      const list = Array.isArray(state.notifications) ? state.notifications : [];
      return list.filter((notification) => notification.context === context);
    },
    [state.notifications]
  );

  const getUnreadByContext = useCallback(
    (context: string) => {
      const list = Array.isArray(state.notifications) ? state.notifications : [];
      return list.filter((notification) => notification.context === context && !notification.isRead).length;
    },
    [state.notifications]
  );

  // Register this browser for FCM web push (works even when the tab is closed) once
  // an admin is logged in. No-op if Firebase web env vars aren't set — Socket.IO + the
  // bell still provide live in-app notifications.
  // The returned controls are exposed via context so <EnablePushButton /> can drive
  // the opt-in prompt and hide itself once permission is granted.
  const push = usePushRegistration(!!user, () => {
    // On a foreground push, refresh the unread badge so the bell stays in sync.
    getUnreadCount('admin_panel');
  });

  // Clicking a background notification while the panel is already open focuses
  // this tab; this navigates it to the notification's destination (`data.url`).
  useNotificationClickBridge();

  // ─── Push opt-in dialog (single, app-level) ───────────────────────────────
  const [pushDialogOpen, setPushDialogOpen] = useState(false);
  const openPushDialog = useCallback(() => setPushDialogOpen(true), []);
  const closePushDialog = useCallback(() => setPushDialogOpen(false), []);
  const autoPromptedRef = useRef(false);

  // Auto-open the opt-in dialog on every page load when notifications are NOT enabled
  // (permission 'default' or 'denied') for a logged-in, push-capable admin. Once per
  // page load (autoPromptedRef resets on a full reload, so it re-asks next load).
  useEffect(() => {
    if (autoPromptedRef.current) return;
    if (!user) return;
    if (!push.configured || !push.supported) return;
    if (push.permission === 'granted') return;

    autoPromptedRef.current = true;
    const t = setTimeout(() => setPushDialogOpen(true), 900);
    return () => clearTimeout(t);
  }, [user, push.configured, push.supported, push.permission]);

  // Effects: live in-app notifications via Socket.IO (re-enabled) + browser permission.
  // Depends ONLY on the user id — connect/disconnect are stable (empty deps) and the
  // other calls run once here. Depending on the callbacks (whose identity changes when
  // state.stats updates) caused an infinite connect→disconnect→reconnect loop
  // ("User authenticated" spam + push storm). Keep this dep list minimal on purpose.
  useEffect(() => {
    if (user) {
      connectSocket();
      getUnreadCount('admin_panel');
      fetchNotifications('admin_panel');
      // NOTE: no auto-prompt for notification permission — the prompt is driven by
      // <EnablePushButton /> so the admin opts in deliberately from a modal.
    } else {
      disconnectSocket();
    }

    return () => {
      disconnectSocket();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const contextValue: NotificationContextType = {
    ...state,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    getUnreadCount,
    connectSocket,
    disconnectSocket,
    getNotificationsByContext,
    getUnreadByContext,
    pushPermission: push.permission,
    pushSupported: push.supported,
    pushConfigured: push.configured,
    pushBusy: push.busy,
    enablePush: push.enablePush,
    pushDialogOpen,
    openPushDialog,
    closePushDialog,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <EnablePushDialog />
    </NotificationContext.Provider>
  );
};
